import React from "react";
import {
  Layers,
  CreditCard,
  RotateCcw,
  Gavel,
  Database,
  ArrowLeftRight,
  Package,
  Wrench,
  HeartHandshake,
  ArrowLeft,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ICONS = {
  CreditCard,
  RotateCcw,
  Gavel,
  Database,
  ArrowLeftRight,
  Package,
  Wrench,
  HeartHandshake
};

export default function TopicSelect({ topics, mixedCount, onSelect, onBack }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="text-center mb-10">
        <span className="text-xs font-medium tracking-widest uppercase text-primary">
          General knowledge check
        </span>
        <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
          Pick a topic
        </h1>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-muted-foreground">
          Run a mixed session across all topics, or focus on a specific area.
        </p>
      </div>

      {/* Mixed featured card */}
      <button
        type="button"
        onClick={() => onSelect("mixed")}
        className="group w-full text-left p-6 rounded-2xl border border-primary/30 bg-primary/5 hover:border-primary hover:bg-primary/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg mb-5"
      >
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary flex-shrink-0">
            <Layers className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-foreground leading-snug">
              Mixed (all topics)
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              A representative mix sampled from every topic — billing, refunds,
              legal, systems, CC flows, products, tech, and retention.
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
            <span className="text-xs font-medium text-muted-foreground">
              {mixedCount} questions
            </span>
            <span className="inline-flex items-center text-sm font-medium text-primary">
              Start
              <ArrowRight className="ml-1.5 w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </button>

      {/* Topic grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {topics.map((t) => {
          const Icon = ICONS[t.icon] || Layers;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onSelect(t.key)}
              className="group text-left p-5 rounded-2xl border border-border bg-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-accent"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-accent/10 text-accent mb-3">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h3 className="text-base font-semibold text-foreground leading-snug">
                {t.label}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {t.desc}
              </p>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t.count} questions
                </span>
                <span className="inline-flex items-center text-sm font-medium text-accent">
                  Start
                  <ArrowRight className="ml-1.5 w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}